import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Project } from "@reina/shared";
import {
  Button,
  EmptyState,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  TableWrap,
} from "../../components";
import { useProjects } from "../../api/pricings";
import { errorMessage, formatDate } from "../../lib/format";
import { ProjectModal } from "./ProjectModal";

export function ProjectsPage() {
  const navigate = useNavigate();
  const projects = useProjects();
  const items = projects.data?.items ?? [];
  const [modal, setModal] = useState<{ project: Project | null } | null>(null);

  const newButton = (
    <Button variant="primary" onClick={() => setModal({ project: null })}>
      New project
    </Button>
  );

  return (
    <>
      <Panel>
        <PanelHead
          title="Projects"
          sub={
            projects.data
              ? `${projects.data.total} project${projects.data.total === 1 ? "" : "s"}`
              : "projects → named, dated pricings → buy-list comparisons"
          }
          actions={newButton}
        />
        {projects.isPending ? (
          <PanelBody>
            <EmptyState title="Loading projects…" />
          </PanelBody>
        ) : projects.isError ? (
          <PanelBody>
            <EmptyState title="Could not load projects" hint={errorMessage(projects.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No projects yet"
              hint="Create a project, then add a named pricing and paste a buy-list."
              action={newButton}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Project</th>
                <th>Client / developer</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((project) => (
                <tr
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <strong>{project.name}</strong>
                    {project.description && <span className="sub">{project.description}</span>}
                  </td>
                  <td>{project.clientDeveloper ?? "—"}</td>
                  <td>
                    <Pill tone={project.status === "active" ? "ok" : "neutral"}>
                      {project.status === "active" ? "Active" : "Archived"}
                    </Pill>
                  </td>
                  <td className="mono">{formatDate(project.createdAt)}</td>
                  <td>
                    <Button
                      mini
                      onClick={(event) => {
                        event.stopPropagation();
                        setModal({ project });
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {modal && (
        <ProjectModal
          key={modal.project?.id ?? "new"}
          project={modal.project}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
